"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.registerSection = registerSection;
exports.getManifest = getManifest;
exports.listManifests = listManifests;
exports._resetRegistry = _resetRegistry;
exports.sectionsForLine = sectionsForLine;
exports.starterHomeSections = starterHomeSections;
exports.validateSection = validateSection;
const REGISTRY = new Map();
function registerSection(m) { REGISTRY.set(m.type, m); }
function getManifest(type) { return REGISTRY.get(type); }
function listManifests() { return Array.from(REGISTRY.values()); }
/** Test-only: clear the registry between unit tests. */
function _resetRegistry() { REGISTRY.clear(); }
/**
 * The ONE place the editor and the AI site-builder read to decide which sections
 * a business may add — so they can never disagree.
 *  - Ruby Estate: every section except category `products` (shop-only), plus the
 *    Estate-only `rentals` category (`listings`).
 *  - Ruby Till (missing/'rubytill'): every section exactly as today — `rentals`
 *    stays hidden.
 */
function sectionsForLine(line) {
    const all = listManifests();
    return line === 'rubyestate'
        ? all.filter((m) => m.category !== 'products')
        : all.filter((m) => m.category !== 'rentals');
}
function starterSection(type, overrides = {}, blocks = []) {
    const m = getManifest(type);
    return { id: `starter_${type}`, type, visible: true, settings: { ...(m?.defaults() ?? {}), ...overrides }, blocks };
}
/**
 * The home-page sections a brand-new/empty theme is seeded with. Ruby Till's seed
 * is today's seed, byte-for-byte (mirrors `theme-migrate.ts`'s legacy default
 * order for a tenant with no saved homepage settings). Ruby Estate gets a
 * rental-oriented starter instead — "same tools", different starting content
 * (owner direction 2026-10-08).
 */
function starterHomeSections(line) {
    if (line === 'rubyestate') {
        return [
            starterSection('hero', { headline: 'Homes and spaces for rent', ctaLabel: 'View rentals', ctaHref: '/rentals' }),
            starterSection('listings', { heading: 'Available now', limit: 6 }),
            starterSection('rich-text', {
                title: 'About us',
                body: 'Tell renters about your properties, your team, and what makes you a landlord worth renting from.',
            }),
            starterSection('testimonials'),
            starterSection('faq', {}, [
                { id: 'faq_apply', type: 'item', settings: { question: 'How do I apply?',
                        answer: 'Find a listing you like and select "Apply online" to start your application.' } },
                { id: 'faq_need', type: 'item', settings: { question: 'What do I need to apply?',
                        answer: 'A government ID, proof of income, and your rental history help us process your application quickly.' } },
                { id: 'faq_pay', type: 'item', settings: { question: 'How do I pay rent?',
                        answer: 'Once approved, residents can pay rent online from their resident account each month.' } },
                { id: 'faq_repair', type: 'item', settings: { question: 'How do I request a repair?',
                        answer: 'Residents can submit a maintenance request from their account and track its progress.' } },
            ]),
            starterSection('newsletter'),
        ];
    }
    // Ruby Till — unchanged. Literal values mirror theme-migrate.ts's ORDER-based
    // seed for a settingsItem with no `homepage` saved (sectionFromLegacy(type, {})).
    return [
        { id: 'mig_hero', type: 'hero', visible: true,
            settings: { headline: '', subtext: '', imageUrl: '', ctaLabel: 'Shop the catalogue', ctaHref: '/category/all' }, blocks: [] },
        { id: 'mig_info-strip', type: 'info-strip', visible: true, settings: {}, blocks: [] },
        { id: 'mig_category-grid', type: 'category-grid', visible: true,
            settings: { title: 'Shop by category', eyebrow: 'Browse', limit: 12, categoryIds: [] }, blocks: [] },
        { id: 'mig_featured-collection', type: 'featured-collection', visible: true,
            settings: { title: 'Favourites this week', eyebrow: 'Handpicked', limit: 8, productIds: [] }, blocks: [] },
        { id: 'mig_rich-text', type: 'rich-text', visible: true,
            settings: { title: '', body: '', align: 'center' }, blocks: [] },
        { id: 'mig_newsletter', type: 'newsletter', visible: true,
            settings: { title: 'First to the new arrivals.', subtext: 'One short email a month.' }, blocks: [] },
    ];
}
function validateSection(instance) {
    const m = getManifest(instance.type);
    if (!m)
        return { ok: false, error: `unknown_section_type:${instance.type}` };
    const parsed = m.settingsSchema.safeParse(instance.settings ?? {});
    if (!parsed.success)
        return { ok: false, error: `invalid_settings:${instance.type}` };
    const blocks = [];
    for (const b of instance.blocks ?? []) {
        const bt = m.blockTypes?.[b.type];
        if (!bt)
            return { ok: false, error: `unknown_block_type:${instance.type}.${b.type}` };
        const bp = bt.settingsSchema.safeParse(b.settings ?? {});
        if (!bp.success)
            return { ok: false, error: `invalid_block:${instance.type}.${b.type}` };
        blocks.push({ ...b, settings: bp.data });
    }
    return { ok: true, settings: parsed.data, blocks };
}
//# sourceMappingURL=manifest.js.map